import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/company";

const sanitize = (s) => (s || "").replace(/[/\\?%*:|"<>]/g, "").trim().replace(/\s+/g, " ");

const ProductDatasheetPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [autoPrinted, setAutoPrinted] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/products/${id}`)
      .then(({ data }) => setP(data))
      .catch(() => {
        toast.error("Product not found");
        nav("/products");
      });
  }, [id, nav]);

  const doPrint = () => {
    if (!p) return;
    const filename = `${sanitize(p.model_number)}-DATASHEET`.trim();
    const original = document.title;
    document.title = filename || original;
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = original; }, 1000);
    }, 50);
  };

  // Auto-trigger print dialog once, when arriving from the "Generate PDF" button
  useEffect(() => {
    if (!p || autoPrinted) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoprint") === "1") {
      setAutoPrinted(true);
      setTimeout(doPrint, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p, autoPrinted]);

  if (!p) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

  const imgs = p.images && p.images.length > 0 ? p.images : [];

  return (
    <div className="bg-slate-100 p-0">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b border-[#E5E7EB] bg-white px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-sm"
          onClick={() => nav("/products")}
          data-testid="back-to-products"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Data Sheets
        </Button>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          Product Datasheet — {p.model_number}
        </div>
        <Button
          size="sm"
          className="rounded-sm ep-amber hover:ep-amber"
          onClick={doPrint}
          data-testid="print-product-datasheet-button"
        >
          <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
        </Button>
      </div>

      <div className="mx-auto my-6 bg-white shadow-md pdf-page" data-testid="product-datasheet-preview">
        {/* Letterhead */}
        <div className="flex items-center justify-between gap-6 border-b-2 border-[#1E40AF] pb-4">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logoSrc} alt="Excellent Plastic" className="h-16 w-16 object-contain" />
            <div>
              <div className="font-heading text-2xl font-extrabold uppercase leading-none text-[#1E40AF]">
                Excellent Plastic
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">
                Materials Trading LLC
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-bold uppercase tracking-wider text-[#1E40AF]">
              Product Datasheet
            </div>
            <div className="mt-2 text-xs">
              <div>
                <span className="text-slate-500">Model:</span>{" "}
                <span className="font-semibold">{p.model_number}</span>
              </div>
              {p.category && (
                <div>
                  <span className="text-slate-500">Category:</span> {p.category}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product Title */}
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</div>
          <div className="mt-1 border-l-4 border-[#22C55E] pl-3 text-lg font-bold text-slate-800">
            {p.name || p.model_number}
          </div>
        </div>

        {/* Body — image + specs */}
        {/* Body — specs table (full width), then images below */}
        <div className="mt-5">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Technical Specifications
          </div>
          {(p.specs || []).length === 0 ? (
            <div className="rounded-sm border border-dashed border-[#E5E7EB] bg-slate-50 p-4 text-xs text-slate-500">
              No technical specifications recorded for this product.
            </div>
          ) : (
            <table className="w-full border-collapse text-[12px]">
              <tbody>
                <tr>
                  <td className="w-56 border border-[#E5E7EB] bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    Model Number
                  </td>
                  <td className="border border-[#E5E7EB] px-3 py-1.5 text-slate-700">{p.model_number}</td>
                </tr>
                {p.specs.map((sp, sIdx) => (
                  <tr key={sIdx}>
                    <td className="w-56 border border-[#E5E7EB] bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      {sp.label || "—"}
                    </td>
                    <td className="border border-[#E5E7EB] px-3 py-1.5 text-slate-700">
                      {sp.value || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Product Images — below specs, only if images exist */}
        {imgs.length > 0 && (
          <div className="mt-6" data-testid="product-images-gallery" style={{ pageBreakInside: "avoid" }}>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {imgs.length === 1 ? "Product Image" : `Product Images (${imgs.length})`}
            </div>
            <div
              className={
                imgs.length === 1
                  ? "flex justify-center"
                  : "grid gap-3 " + (imgs.length === 2 ? "grid-cols-2" : "grid-cols-3")
              }
            >
              {imgs.map((src, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center border border-[#E5E7EB] bg-white p-1.5"
                  style={{ pageBreakInside: "avoid" }}
                >
                  <img
                    src={src}
                    alt={p.model_number}
                    style={{
                      maxWidth: "100%",
                      width: imgs.length === 1 ? "7cm" : "100%",
                      height: imgs.length === 1 ? "5.5cm" : "4.5cm",
                      objectFit: "contain",
                      display: "block",
                      background: "#fff",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDatasheetPage;
